import React, { useState, useEffect } from 'react';
import Style from './EnhancedTerminal.module.scss';
import { Box, Typography } from "@mui/material";
import { info } from "../../info/Info";
import { motion } from "framer-motion";

export default function About({ innerRef }) {
  const firstName = info.firstName.toLowerCase();
  const [terminals, setTerminals] = useState({
    bio: { visible: false, content: null },
    skills: { visible: false, content: null },
    hobbies: { visible: false, content: null }
  });
  
  // Pre-render all content once on mount
  useEffect(() => {
    // Pre-render all terminal content
    setTerminals({
      bio: { 
        visible: false, 
        content: aboutMeText() 
      },
      skills: { 
        visible: false, 
        content: skillsText() 
      },
      hobbies: { 
        visible: false, 
        content: miscText() 
      }
    });

    // Sequence terminal visibility
    const timer1 = setTimeout(() => {
      setTerminals(prev => ({
        ...prev,
        bio: { ...prev.bio, visible: true }
      }));
    }, 500);
    
    const timer2 = setTimeout(() => {
      setTerminals(prev => ({
        ...prev,
        skills: { ...prev.skills, visible: true }
      }));
    }, 1200);
    
    const timer3 = setTimeout(() => {
      setTerminals(prev => ({
        ...prev,
        hobbies: { ...prev.hobbies, visible: true }
      }));
    }, 1900);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  function aboutMeText() {
    return (
      <div className={Style.terminalContent}>
        <p>
          <span className={Style.prompt}>{firstName}{info.lastName.toLowerCase()} $</span>
          <span className={Style.command}> cat about{firstName}</span>
        </p>
        <div className={Style.output}>
          <TypeWriter text={info.bio} />
        </div>
      </div>
    );
  }

  function skillsText() {
    return (
      <div className={Style.terminalContent}>
        <p>
          <span className={Style.prompt}>{firstName}{info.lastName.toLowerCase()} $</span>
          <span className={Style.command}> cd skills && ls -la</span>
        </p>
        <div className={Style.output}>
          <Typography variant="body1" className={Style.sectionTitle}>
            LANGUAGES & FRAMEWORKS
          </Typography>
          <div className={Style.skillGrid}>
            {info.skills.proficientWith.map((proficiency, index) => (
              <div key={index} className={Style.skillItem}>
                <span className={Style.skillIcon}>⚡</span> {proficiency}
              </div>
            ))}
          </div>
          
          <Typography variant="body1" className={Style.sectionTitle} style={{ marginTop: '1.5rem' }}>
            TOOLS & PLATFORMS
          </Typography>
          <div className={Style.skillGrid}>
            {info.skills.exposedTo.map((skill, index) => (
              <div key={index} className={Style.skillItem}>
                <span className={Style.skillIcon}>🔧</span> {skill}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function miscText() {
    return (
      <div className={Style.terminalContent}>
        <p>
          <span className={Style.prompt}>{firstName}{info.lastName.toLowerCase()} $</span>
          <span className={Style.command}> cd interests && ./show-hobbies.sh</span>
        </p>
        <div className={Style.output}>
          <div className={Style.hobbies}>
            {info.hobbies.map((hobby, index) => (
              <div key={index} className={Style.hobbyItem}>
                <span className={Style.hobbyEmoji}>{hobby.emoji}</span>
                <span className={Style.hobbyLabel}>{hobby.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Terminal component definition
  const Terminal = ({ title, isVisible, children }) => (
    <div className={Style.terminalWrapper} style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.5s ease, transform 0.5s ease' }}>
      <Box className={Style.terminal}>
        <Box className={Style.terminalHeader}>
          <Box className={Style.terminalButtons}>
            <span className={Style.closeBtn}></span>
            <span className={Style.minimizeBtn}></span>
            <span className={Style.maximizeBtn}></span>
          </Box>
          <Box className={Style.terminalTitle}>
            {title}.sh
          </Box>
        </Box>
        <Box className={Style.terminalBody}>
          {children}
        </Box>
      </Box>
    </div>
  );

  return (
    <div
      ref={innerRef}
      className={Style.aboutContainer}
      id="about"
      style={{ 
        minHeight: '1000px',  // Reserve space for all content
        transition: 'opacity 0.5s ease',
        opacity: 1
      }}
    >
      {/* All terminals are rendered from the start with fixed height/positioning */}
      {/* This prevents layout shifts and reflows */}
      <Terminal 
        title="about-me" 
        isVisible={terminals.bio.visible}
      >
        {terminals.bio.content}
      </Terminal>
      
      <Terminal 
        title="skills" 
        isVisible={terminals.skills.visible}
      >
        {terminals.skills.content}
      </Terminal>
      
      <Terminal 
        title="interests" 
        isVisible={terminals.hobbies.visible}
      >
        {terminals.hobbies.content}
      </Terminal>
    </div>
  );
}

// Simple typewriter effect component
function TypeWriter({ text }) {
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 10); // Speed of typing
      
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text]);
  
  return <p className={Style.typewriter}>{displayText}<span className={Style.cursor}>_</span></p>;
}